import type { Timecard } from '../types';
import { formatCompanyDate } from './companyDateFormat';
import type { TimecardReportCommonFilters } from './timecardReportFilters';
import { filterTimecardsByCommonFilters, filterTimecardsByWeekRange, formatCommonFiltersSummary } from './timecardReportFilters';
import { formatWeekRangeLabel } from './timecardReportFilters';

export interface WeeklyTotalsFilterParams extends TimecardReportCommonFilters {
  fromWeekStartDate: string;
  toWeekStartDate: string;
}

export function filterTimecardsForWeeklyTotals(
  timecards: Timecard[],
  params: WeeklyTotalsFilterParams
): Timecard[] {
  const common = filterTimecardsByCommonFilters(timecards, params);
  return filterTimecardsByWeekRange(common, params.fromWeekStartDate, params.toWeekStartDate);
}

export function generateWeeklyTotalsData(
  timecards: Timecard[],
  fromWeekStart: string,
  toWeekStart: string
) {
  const fromDate = new Date(fromWeekStart);
  const toDate = new Date(toWeekStart);

  const employeeData = new Map<number, { name: string; weeks: Map<string, number> }>();

  timecards.forEach((timecard) => {
    if (!employeeData.has(timecard.employeeId)) {
      const employeeName = timecard.employee
        ? `${timecard.employee.firstName} ${timecard.employee.lastName}`
        : `Employee ${timecard.employeeId}`;

      employeeData.set(timecard.employeeId, {
        name: employeeName,
        weeks: new Map(),
      });
    }

    const empData = employeeData.get(timecard.employeeId)!;
    const timecardWeekDate = new Date(timecard.weekStartDate);
    const weekNumber = Math.floor(
      (timecardWeekDate.getTime() - fromDate.getTime()) / (7 * 24 * 60 * 60 * 1000)
    );

    const totalHours =
      timecard.days?.reduce((sum, day) => sum + (day.totalHours || 0), 0) || 0;

    if (weekNumber >= 0 && weekNumber < 13) {
      empData.weeks.set(`week_${weekNumber}`, totalHours);
    }
  });

  const headers = ['Employee Name'];

  for (let i = 0; i < 13; i++) {
    const weekDate = new Date(fromDate);
    weekDate.setDate(weekDate.getDate() + i * 7);
    const isInRange = weekDate >= fromDate && weekDate <= toDate;

    if (isInRange) {
      headers.push(
        `Week ${i + 1} (${formatCompanyDate(weekDate)})`
      );
    } else {
      headers.push(`Week ${i + 1}`);
    }
  }

  headers.push('Total');

  const rows = Array.from(employeeData.values()).map((empData) => {
    const weekValues = Array.from({ length: 13 }, (_, i) => {
      const hours = empData.weeks.get(`week_${i}`) || 0;
      return hours.toFixed(2);
    });
    const total = weekValues.reduce((sum, val) => sum + parseFloat(val), 0).toFixed(2);
    return [empData.name, ...weekValues, total];
  });

  rows.sort((a, b) => a[0].localeCompare(b[0]));

  return { headers, rows };
}

export function formatWeeklyTotalsFilterSummary(parameters: WeeklyTotalsFilterParams): string {
  const parts: string[] = [];

  if (parameters.fromWeekStartDate && parameters.toWeekStartDate) {
    parts.push(
      `${formatWeekRangeLabel(parameters.fromWeekStartDate)} → ${formatWeekRangeLabel(parameters.toWeekStartDate)}`
    );
  }

  const common = formatCommonFiltersSummary(parameters);
  if (common) parts.push(common);

  return parts.join(' · ') || 'All records';
}
